/**
 * THE UNREAD MARKER ON ONE BELL ROW — a dot, and deliberately never a number.
 *
 * THE BADGE COUNTS ROWS (OSO-586). One row of the list is one point of
 * `Me.badges.unreadNotificationCount`, whether it collapses one comment or
 * ten, so the member can count the dots on the screen and arrive at the number
 * on the bell. That is the whole contract, and a "10" drawn on a row that
 * counts 1 towards the badge would break it — which is why `unreadCount`
 * arrives on the wire and is not rendered: it exists so the client knows a
 * partly-cleared row is still unread (`setUnreadCount`,
 * lib/hooks/useNotifications.ts), not so it can be printed.
 *
 * It used to be printed, for the opposite reason: the badge counted raw
 * notifications, so the number was the only thing that made the two reconcile
 * (OSO-524). The count moved, so the number is no longer needed to explain it.
 *
 * ONE COMPONENT BECAUSE THERE ARE TWO LISTS. The dropdown in `SiteHeader` and
 * the full page at `/notifikationer` render the same row and had the same
 * hand-written dot in both files.
 */
export function UnreadMarker({ count }: { count: number }) {
  if (count <= 0) return null;
  return <Dot label="Ulæst" />;
}

/**
 * THE MARKS THEMSELVES — one definition each, because they are drawn in seven
 * places between them
 *
 * `Dot` is the 10px blue dot: this file's unread mark and the DM inbox's
 * "unread messages" mark (`DmSignal.tsx`) were the same span written twice. `Ring` is its hollow twin — same colour, same size, the quieter
 * member of one family. `CountBadge` is the red pill: it was hand-rolled in
 * SiteHeader twice, in ChatDock once and in `DmSignal` again, `99+` cap
 * included, which is how the header came to draw two badges side by side from
 * two sources of truth.
 *
 * `role="img"` IS NOT DECORATION. ARIA prohibits `aria-label` on a generic
 * element and screen readers do not expose it, so an empty `<span aria-label>`
 * passes `getByLabelText` in a test and says nothing at all to the person who
 * needs it. Every mark here names itself through a role that can carry a name.
 */
export function Dot({ label, className }: { label: string; className?: string }) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`w-2.5 h-2.5 bg-blue-500 rounded-full shrink-0${className ? ` ${className}` : ""}`}
    />
  );
}

export function Ring({ label, className }: { label: string; className?: string }) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`w-2.5 h-2.5 border-2 border-blue-500 rounded-full shrink-0${className ? ` ${className}` : ""}`}
    />
  );
}

/**
 * The red count pill on an icon or a nav item. `label` is optional: on a
 * button that already says "Notifikationer" the number is decoration, and a
 * second announcement of it is noise.
 */
export function CountBadge({
  count,
  label,
  className,
}: {
  count: number;
  label?: string;
  className?: string;
}) {
  if (count <= 0) return null;
  return (
    <span
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
      className={`min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full${className ? ` ${className}` : ""}`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}
